import React, { forwardRef, useId } from "react";
import * as Checkbox from "@radix-ui/react-checkbox";
import styled from "styled-components";
import { CheckIcon } from "assets/icons";

export interface CheckBoxProps extends Checkbox.CheckboxProps {
  id?: string;
  children?: React.ReactNode;
}

const CheckBox = forwardRef<HTMLButtonElement, CheckBoxProps>(
  ({ children, disabled = false, ...args }, ref) => {
    const customId = useId();

    return (
      <CheckBoxLayout>
        <CheckBoxRoot id={customId} disabled={disabled} ref={ref} {...args}>
          <Checkbox.Indicator>
            <CheckIcon />
          </Checkbox.Indicator>
        </CheckBoxRoot>
        <CheckBoxLabel htmlFor={customId} disabled={disabled}>
          {children}
        </CheckBoxLabel>
      </CheckBoxLayout>
    );
  },
);

CheckBox.displayName = "CheckBox";

export default CheckBox;

const CheckBoxLayout = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
`;

const CheckBoxRoot = styled(Checkbox.Root)`
  background-color: ${({ theme }) => theme.color.white};
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.color.grey[400]};
  position: relative;

  width: 2.4rem;
  height: 2.4rem;

  svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &[data-state="checked"] {
    svg {
      color: ${({ theme }) => theme.color.brick[500]};
    }

    background-color: ${({ theme }) => theme.color.brick[200]};
  }

  :disabled {
    border: 1px solid ${({ theme }) => theme.color.grey[300]};

    &[data-state="checked"] {
      svg {
        color: ${({ theme }) => theme.color.white};
      }
      background-color: ${({ theme }) => theme.color.grey[300]};
    }
  }
`;

const CheckBoxLabel = styled.label<{
  disabled: boolean;
}>`
  ${({ theme }) => theme.typography.body1.regular};
  ${({ disabled, theme }) => disabled && `color: ${theme.color.grey[400]}`}
  cursor: pointer;
`;
